import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes, useTheme } from 'styled-components';
import { closeModal, openModal } from '../../redux/slices/modals';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import {
  useCreateCustomGiftCardDesignMutation,
  useDeleteCustomGiftCardDesignMutation,
  useGetCustomGiftCardDesignsQuery,
} from '../../redux/api/authorized/customGiftCardDesigns';
import { selectPrivyToken } from '../../redux/slices/auth';
import { hapticsImpactLight } from '../../common/haptic';
import { selectThemeMode } from '../../redux/slices/themeSlice';
import ChevronLeftIcon from '../../assets/icons/chevron-left.svg?react';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import { compressBase64Image } from '../../common/images';

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 516px;
  margin: 0 auto;
  overflow: hidden;

  @media ${PHONE_MEDIA_QUERY} {
    max-width: 407px;
  }
`;

const CardContainer = styled.div`
  width: 100%;
  aspect-ratio: 407 / 226;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 15px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background-image: ${({ $background }) =>
    $background ? `url(${$background})` : 'none'};
  background-size: cover;
  background-position: center;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 8px;
    border-radius: 8px;
  }
`;

const CardRow = styled.div``;

const TokenContainer = styled.div`
  background-color: ${({ theme }) => theme.background.secondary};
  display: flex;
  gap: 8px;
  padding: 10px;
  border-radius: 16px;
  align-items: center;
  width: 100%;

  @media ${PHONE_MEDIA_QUERY} {
    padding: 4px 12px;
    border-radius: 8px;
  }
`;

const TokenPrice = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

const TokenPriceLabel = styled.p`
  font-size: 11px;
  font-weight: 400;
  color: ${({ theme }) => theme.text.secondary};
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 10px;
  }
`;

const TokenLogo = styled.img`
  width: 36px;
  height: 36px;
  margin-left: auto;
  flex-shrink: 0;
  @media ${PHONE_MEDIA_QUERY} {
    width: 28px;
    height: 28px;
  }
`;

const Container = styled.div`
  width: 100%;
  display: flex;
`;

const NextAndPrevious = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 30px;
`;

const GiftCardImageSelectorScroll = styled.div`
  width: 100%;
  max-width: 516px;
  margin: 20px auto;
  margin-bottom: 0px;
  user-select: none;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 407px;
  }
`;

const GiftCardImageSelector = styled.div`
  display: flex;
  gap: 10px;
  width: fit-content;
  padding: 0 0 0 5px;
`;

const GiftCardImageOption = styled.div`
  width: 64px;
  height: 64px;
  padding: 2px;
  box-shadow: 0px 0px 6px 0px ${({ theme }) => theme.text.regular};
  border-radius: 999px;
  cursor: pointer;
  @media ${PHONE_MEDIA_QUERY} {
    width: 48px;
    height: 48px;
  }
  background-color: ${({ theme }) => theme.background.primary};
  border: 1px solid
    ${({ selected, theme }) =>
      selected ? theme.primary.primary : 'transparent'};
  position: relative;
`;

const GiftCardImageOptionSelected = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.background.overlay};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GiftCardImageOptionImage = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
`;

export const GiftCardEditorContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const GiftCard = ({
  cardImage,
  token,
  usdAmount,
  tokenAmount,
  editable = false,
  onCardImageChange,
  hideValue = false,
}) => {
  const dispatch = useDispatch();

  const [selectedDesign, setSelectedDesign] = useState(0);
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const privyToken = useSelector(selectPrivyToken);

  const { data: customGiftCardDesigns, refetch: refetchCustomGiftCardDesigns } =
    useGetCustomGiftCardDesignsQuery({
      skip: !privyToken,
    });

  const [createCustomGiftCardDesign] = useCreateCustomGiftCardDesignMutation();
  const [deleteCustomGiftCardDesign] = useDeleteCustomGiftCardDesignMutation();

  const customDesigns = customGiftCardDesigns?.customGiftCardDesigns
    ? customGiftCardDesigns?.customGiftCardDesigns.map((design) => ({
        imageUrl: design.url,
      }))
    : [];

  let firstImage = [];

  if (token?.giftCardImages) {
    if (token?.giftCardImages[0]?.imageUrl?.includes('default-giftcard.png')) {
      firstImage = [];
    } else {
      firstImage = [token?.giftCardImages[0]];
    }
  }

  const defaultGiftCardImages = [
    ...firstImage,
    { imageUrl: '/BIRTHDAY.webp' },
    { imageUrl: '/THANK_YOU.webp' },
    ...(firstImage.length > 0 && isMobile
      ? []
      : [{ imageUrl: '/ANNIVERSARY.webp' }]),
    { imageUrl: '/JUST_BECAUSE.webp' },
  ];

  const availableGiftCardImages = [...defaultGiftCardImages, ...customDesigns];

  useEffect(() => {
    if (cardImage) {
      for (let i = 0; i < availableGiftCardImages?.length; i++) {
        let image = availableGiftCardImages[i];
        if (image?.imageUrl === cardImage) {
          setSelectedDesign(i);
        }
      }
    }
  }, [cardImage, availableGiftCardImages]);

  useEffect(() => {
    if (!cardImage) {
      onCardImageChange(availableGiftCardImages[0]?.imageUrl);
    }
  }, [cardImage, availableGiftCardImages, onCardImageChange]);

  useEffect(() => {
    if (privyToken) {
      refetchCustomGiftCardDesigns();
    }
  }, [privyToken]);

  const themeMode = useSelector(selectThemeMode);

  const theme = useTheme();

  return (
    <>
      <Container>
        {editable && !isMobile && (
          <NextAndPrevious
            onClick={() => {
              onCardImageChange(
                selectedDesign === 0
                  ? availableGiftCardImages[availableGiftCardImages.length - 1]
                      ?.imageUrl
                  : availableGiftCardImages[selectedDesign - 1]?.imageUrl,
              );
            }}
          >
            <ChevronLeftIcon width={9} height={48} color={theme.text.title} />
          </NextAndPrevious>
        )}
        <CardWrapper>
          <CardContainer $background={cardImage}>
            <CardRow style={{ marginTop: 'auto' }}>
              {hideValue ? (
                <></>
              ) : (
                <TokenContainer>
                  <div>
                    <TokenPriceLabel>Gift Card Value</TokenPriceLabel>
                    <TokenPrice>
                      {usdAmount ? `$${usdAmount} of` : tokenAmount}{' '}
                      {token?.symbol}
                    </TokenPrice>
                  </div>
                  <TokenLogo src={token?.logoURI} />
                </TokenContainer>
              )}
            </CardRow>
          </CardContainer>
        </CardWrapper>
        {editable && !isMobile && (
          <NextAndPrevious
            onClick={() => {
              onCardImageChange(
                selectedDesign === availableGiftCardImages.length - 1
                  ? availableGiftCardImages[0]?.imageUrl
                  : availableGiftCardImages[selectedDesign + 1]?.imageUrl,
              );
            }}
          >
            <ChevronRightIcon width={9} height={48} color={theme.text.title} />
          </NextAndPrevious>
        )}
      </Container>
      {editable && (
        <GiftCardImageSelectorScroll>
          <GiftCardImageSelector>
            {availableGiftCardImages.map((giftCardImage, index) => (
              <GiftCardImageOption
                selected={selectedDesign === index}
                key={index}
                onClick={() => {
                  hapticsImpactLight();
                  onCardImageChange(giftCardImage?.imageUrl);
                }}
              >
                <GiftCardImageOptionImage
                  src={giftCardImage?.imageUrl}
                  alt='Gift Card'
                />
                {selectedDesign === index && (
                  <GiftCardImageOptionSelected
                    onClick={() => {
                      hapticsImpactLight();
                      if (index > token?.giftCardImages.length + 3 - 1) {
                        dispatch(
                          openModal({
                            name: 'confirmation',
                            props: {
                              title: 'Delete Gift Card Design',
                              text: 'Are you sure you want to delete this custom gift card design?',
                              doNotClose: true,
                              onContinue: async () => {
                                onCardImageChange(
                                  availableGiftCardImages[0]?.imageUrl,
                                );
                                await deleteCustomGiftCardDesign({
                                  imageUrl: giftCardImage?.imageUrl,
                                });
                                dispatch(openModal({ name: 'giftFlow' }));
                              },
                            },
                          }),
                        );
                      }
                    }}
                  >
                    {index > defaultGiftCardImages.length - 1 && (
                      <img src='/delete.webp' alt='Delete' width={20} />
                    )}
                  </GiftCardImageOptionSelected>
                )}
              </GiftCardImageOption>
            ))}
            <GiftCardImageOption
              onClick={() => {
                hapticsImpactLight();
                dispatch(
                  openModal({
                    name: 'customizeCard',
                    props: {
                      cardSize: { width: 450, height: 450 * 0.618 },
                      onSelectCard: async (card) => {
                        hapticsImpactLight();
                        const compressedImage = await compressBase64Image(card);
                        let response = await createCustomGiftCardDesign({
                          image: compressedImage,
                        });
                        onCardImageChange(response?.data?.imageUrl);
                        dispatch(openModal({ name: 'giftFlow' }));
                      },
                    },
                  }),
                );
              }}
            >
              <GiftCardImageOptionImage
                src={themeMode === 'light' ? '/new.webp' : '/new-dark.webp'}
                alt='New Gift Card'
              />
            </GiftCardImageOption>
          </GiftCardImageSelector>
        </GiftCardImageSelectorScroll>
      )}
    </>
  );
};

export default GiftCard;
